import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import useSEO, { SEO_CONFIG } from '@/hooks/useSEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  useSEO(SEO_CONFIG.contact);
  const [searchParams] = useSearchParams();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    establishment_type: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const sujet = searchParams.get('sujet');
    if (sujet) {
      setFormData((prev) => ({ ...prev, subject: sujet }));
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message envoyé ! Nous vous répondrons très prochainement.');
      setFormData({ name: '', email: '', phone: '', subject: '', establishment_type: '', message: '' });
    } catch (error) {
      toast.error("Erreur lors de l'envoi du message");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="py-24 lg:py-32">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-12">
          <h1 className="text-5xl lg:text-6xl font-heading text-foreground mb-4">Nous contacter</h1>
          <p className="text-lg text-foreground/70">
            Une question, une demande de devis ou un projet d'événement ? Écrivez-nous, nous vous répondrons rapidement. Vous pouvez aussi découvrir <a href="/nos-bieres" className="text-primary font-medium hover:underline">notre gamme de bières</a> ou nos <a href="/points-de-vente" className="text-primary font-medium hover:underline">points de vente en Provence</a>.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-12">
          {/* Formulaire */}
          <form onSubmit={handleSubmit} className="space-y-6 bg-white border border-stone-200 rounded-2xl p-8" data-testid="contact-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="name">Nom complet *</Label>
                <Input id="name" type="text" placeholder="Jean Dupont" value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })} required data-testid="contact-name-input" />
              </div>
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input id="email" type="email" placeholder="jean@example.com" value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })} required data-testid="contact-email-input" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="phone">Téléphone</Label>
                <Input id="phone" type="tel" placeholder="06 XX XX XX XX" value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })} data-testid="contact-phone-input" />
              </div>
              <div>
                <Label htmlFor="establishment_type">Établissement (professionnels)</Label>
                <Input id="establishment_type" type="text" placeholder="Restaurant, bar, caviste..." value={formData.establishment_type}
                  onChange={(e) => setFormData({ ...formData, establishment_type: e.target.value })} data-testid="contact-establishment-input" />
              </div>
            </div>

            <div>
              <Label htmlFor="subject">Sujet</Label>
              <Input id="subject" type="text" placeholder="Objet de votre message" value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })} data-testid="contact-subject-input" />
            </div>

            <div>
              <Label htmlFor="message">Message *</Label>
              <Textarea id="message" placeholder="Votre message..." value={formData.message} rows={6}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })} required data-testid="contact-message-input" />
            </div>

            <Button type="submit" disabled={submitting}
              className="w-full bg-accent text-accent-foreground hover:bg-accent/90 hover:shadow-lg rounded-full py-6 text-lg font-semibold transition-all"
              data-testid="contact-submit-button">
              {submitting ? 'Envoi en cours...' : 'Envoyer le message'}
            </Button>
          </form>

          {/* Coordonnées */}
          <div className="space-y-6">
            <div className="bg-secondary border border-border rounded-2xl p-8 space-y-5">
              <h2 className="text-2xl font-heading text-foreground">Coordonnées</h2>
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-primary mt-1 shrink-0" />
                <div>
                  <p className="font-medium text-foreground">Soif de Provence — Denis BONNELY</p>
                  <p className="text-foreground/70">1688 petite route des Jardins<br />13210 Saint-Rémy-de-Provence</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-primary shrink-0" />
                <a href="tel:+33664822759" className="text-foreground/80 hover:text-primary transition-colors">06 64 82 27 59</a>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-primary shrink-0" />
                <a href="mailto:soifdeprovence@gmail.com" className="text-foreground/80 hover:text-primary transition-colors">soifdeprovence@gmail.com</a>
              </div>
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-primary mt-1 shrink-0" />
                <p className="text-foreground/70">Livraisons professionnelles les mardis et jeudis.</p>
              </div>
            </div>

            <div className="rounded-2xl overflow-hidden border border-border shadow-sm h-64">
              <iframe
                title="Carte Soif de Provence"
                src="https://www.google.com/maps?q=1688%20petite%20route%20des%20Jardins%2C%2013210%20Saint-R%C3%A9my-de-Provence&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
